import { useEffect, useMemo, useRef, useState } from "react";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import RefreshIcon from "@mui/icons-material/Refresh";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PercentIcon from "@mui/icons-material/Percent";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { MPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import { StyledToggleButton, ToggleButtonGroup } from "@plannel/components/ui/ToggleButton";
import Snackbar from "@plannel/components/Snackbar";
import reduxUtil from "@plannel/utils/redux-util";

import FloChartFlow from "./FloChartFlow";
import FloNodeSidebar from "./FloNodeSidebar";
import FloLegend from "./FloLegend";

/**
 * FLO 차트 페이지 (BOM 모델 플로우 차트).
 * z_bom / z_route / z_routing 기반으로 품목·라우트·자원 노드를 그래프로 시각화한다.
 * - 레이아웃 토글: TB(위→아래) / LR(좌→우)
 * - 노드 클릭 → 우측 사이드바에 기본 속성 표시
 */
const FloChart = ({ t, viewName }) => {
  const reduxDispatch = useDispatch();
  // 탭 전환 후 돌아와도 보기 설정/조회 품목이 유지되도록 viewName 기준 redux(viewStates)에서 복원.
  // (마운트 시 1회만 읽음 — 이후는 로컬 state가 소스 오브 트루스)
  const saved = useMemo(() => reduxUtil.getViewState(viewName) ?? {}, [viewName]);

  const [direction, setDirection] = useState(saved.direction ?? "LR");
  const [hideResource, setHideResource] = useState(saved.hideResource ?? false);
  const [resourceExpanded, setResourceExpanded] = useState(saved.resourceExpanded ?? false);
  const [hideRatio, setHideRatio] = useState(saved.hideRatio ?? false);
  const [hideAltSupply, setHideAltSupply] = useState(saved.hideAltSupply ?? false);
  const [refreshKey, setRefreshKey] = useState(0); // 증가 시 레이아웃 재계산(노드 위치 초기화)
  const [selected, setSelected] = useState(null);

  // 루트 품목 필터: itemSearch(입력 ref) → 조회 실행 시 selectedItemCd 로 확정
  const itemSearch = useRef(saved.selectedItemCd ?? "");
  const [selectedItemCd, setSelectedItemCd] = useState(saved.selectedItemCd ?? "");
  // 검색 필터로 처음 조회한 "원래 루트" 품목. 우클릭으로 이동해도 유지 → 돌아가기 기준.
  const [rootItemCd, setRootItemCd] = useState(saved.rootItemCd ?? saved.selectedItemCd ?? "");
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  // 보기 설정/조회 품목이 바뀌면 redux에 저장 → 다음 방문 시 위에서 복원.
  useEffect(() => {
    const reduxState = reduxUtil.createObjectState(viewName, {
      direction,
      hideResource,
      resourceExpanded,
      hideRatio,
      hideAltSupply,
      selectedItemCd,
      rootItemCd,
    });
    reduxDispatch(reduxUtil.updateViewState(reduxState));
  }, [direction, hideResource, resourceExpanded, hideRatio, hideAltSupply, selectedItemCd, rootItemCd, viewName, reduxDispatch]);

  const retrieveFloChart = () => {
    const next = itemSearch.current?.trim() ?? "";
    setSelected(null);
    setSelectedItemCd(next);
    setRootItemCd(next); // 검색 필터로 조회 = 새 원래 루트
  };

  // 품목 노드 우클릭 → "FLO 조회": 그 품목코드를 루트로 재조회(검색창도 동기화). 원래 루트는 유지.
  const handleSearchItem = (code) => {
    const next = (code ?? "").trim();
    if (!next) return;
    itemSearch.current = next; // 검색 자동완성 입력값 동기화
    setSelected(null);
    setSelectedItemCd(next);
  };

  // 다른 화면(예: 자원 간트 차트 우클릭)에서 이 품목으로 FLO 조회 요청 — 이미 열린 탭에도 반영.
  useEffect(() => {
    const onFloSearch = (e) => {
      const code = (e?.detail?.itemCd ?? "").trim();
      if (!code) return;
      itemSearch.current = code;
      setSelected(null);
      setSelectedItemCd(code);
      setRootItemCd(code);
    };
    window.addEventListener("plannel:floSearchItem", onFloSearch);
    return () => window.removeEventListener("plannel:floSearchItem", onFloSearch);
  }, []);

  const handleDirection = (_e, value) => {
    if (value) setDirection(value);
  };

  const handleHideResource = () => setHideResource((prev) => !prev);

  const handleToggleResourceExpand = () => setResourceExpanded((prev) => !prev);

  const handleHideRatio = () => setHideRatio((prev) => !prev);

  const handleHideAltSupply = () => setHideAltSupply((prev) => !prev);

  const handleRefreshLayout = () => setRefreshKey((prev) => prev + 1);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 150px)", gap: 1, p: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {/* 루트 품목 선택 — Enter 또는 검색 아이콘으로 조회 */}
          <MPItemAutocomplete retrieve={retrieveFloChart} searchValue={itemSearch} key="ITEM" sx={{ width: 250 }} />
          {/* 우클릭 등으로 다른 품목으로 드릴인 했을 때만 표시 — 클릭 시 원래 검색 품목으로 복귀 */}
          {rootItemCd && selectedItemCd && selectedItemCd !== rootItemCd && (
            <Tooltip title={t("floBackToSearch", { item: rootItemCd })}>
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                icon={<RestartAltIcon />}
                label={rootItemCd}
                onClick={() => handleSearchItem(rootItemCd)}
                sx={{ maxWidth: 200 }}
              />
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {/* 동작: 레이아웃 초기화 */}
          <Tooltip title={t("floRefreshLayout")}>
            <IconButton size="small" onClick={handleRefreshLayout}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {/* 자원: 펼치기/접기 + 표시/숨기기 */}
          <Tooltip title={t(resourceExpanded ? "floCollapseResource" : "floExpandResource")}>
            {/* 비활성 시 Tooltip 이벤트를 위해 span으로 감쌈 */}
            <span>
              <StyledToggleButton
                size="small"
                value="expandResource"
                selected={resourceExpanded}
                disabled={hideResource}
                onChange={handleToggleResourceExpand}
              >
                {resourceExpanded ? <UnfoldLessIcon fontSize="small" /> : <UnfoldMoreIcon fontSize="small" />}
              </StyledToggleButton>
            </span>
          </Tooltip>
          <Tooltip title={t(hideResource ? "floShowResource" : "floHideResource")}>
            <StyledToggleButton size="small" value="hideResource" selected={hideResource} onChange={handleHideResource}>
              {hideResource ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </StyledToggleButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {/* 표시 옵션: 소비 비율 / 대체 자재 */}
          <Tooltip title={t(hideRatio ? "floShowRatio" : "floHideRatio")}>
            <StyledToggleButton size="small" value="hideRatio" selected={hideRatio} onChange={handleHideRatio}>
              <PercentIcon fontSize="small" />
            </StyledToggleButton>
          </Tooltip>
          <Tooltip title={t(hideAltSupply ? "floShowAltSupply" : "floHideAltSupply")}>
            <StyledToggleButton size="small" value="hideAltSupply" selected={hideAltSupply} onChange={handleHideAltSupply}>
              <AltRouteIcon fontSize="small" />
            </StyledToggleButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {/* 레이아웃 방향 */}
          <ToggleButtonGroup size="small" exclusive value={direction} onChange={handleDirection}>
            <StyledToggleButton value="LR">
              <Tooltip title={t("floLeftRight")}>
                <SwapHorizIcon fontSize="small" />
              </Tooltip>
            </StyledToggleButton>
            <StyledToggleButton value="TB">
              <Tooltip title={t("floTopDown")}>
                <SwapVertIcon fontSize="small" />
              </Tooltip>
            </StyledToggleButton>
          </ToggleButtonGroup>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {/* 도움말: 호버 시 기호/노드 범례 표시 */}
          <Tooltip
            title={<FloLegend />}
            placement="bottom-end"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: "background.paper",
                  color: "text.primary",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: 3,
                  maxWidth: "none",
                  p: 1,
                },
              },
            }}
          >
            <IconButton size="small">
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flex: 1, minHeight: 0, gap: 1 }}>
        <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, position: "relative", overflow: "hidden" }}>
          <FloChartFlow
            itemCd={selectedItemCd}
            direction={direction}
            hideResource={hideResource}
            resourceExpanded={resourceExpanded}
            hideRatio={hideRatio}
            hideAltSupply={hideAltSupply}
            refreshKey={refreshKey}
            onToggleResource={setResourceExpanded}
            onSelectNode={setSelected}
            onSearchItem={handleSearchItem}
            rootItemCd={rootItemCd}
            setSnackInfo={setSnackInfo}
          />
        </Paper>
        <FloNodeSidebar selected={selected} />
      </Box>

      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </Box>
  );
};

export default withTranslation()(FloChart);
